import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface Anomaly {
  id: string;
  detected_at: string;
  anomaly_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  vehicle_id: string | null;
  driver_id: string | null;
  fuel_txn_id: string | null;
  title: string;
  description: string | null;
  expected_value: string | null;
  actual_value: string | null;
  impact_sar: number | null;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed' | 'false_positive';
  resolved_at: string | null;
  resolution_notes: string | null;
  vehicle_plate: string | null;
  vehicle_code: string | null;
  driver_name: string | null;
  fuel_at: string | null;
  fuel_station: string | null;
  fuel_liters: number | null;
  fuel_total_sar: number | null;
}

export interface AnomalyStats {
  open_count: number;
  open_critical: number;
  open_impact_sar: number;
  week_total: number;
  week_resolved: number;
}

export interface RuleConfig {
  rule_code: string;
  rule_name: string;
  description: string | null;
  severity: string;
  threshold_json: any;
  is_active: boolean;
}

export function useAnomalies(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: ['anomalies', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Anomaly[]; total: number; page: number; pageSize: number; totalPages: number;
      }>('/api/v1/anomalies', {
        params: { type_prefix: 'fuel:', ...filters },
      });
      return data;
    },
    staleTime: 30_000,
  });
}

export function useAnomaly(id: string | null) {
  return useQuery({
    queryKey: ['anomaly', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/api/v1/anomalies/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useAnomalyStats() {
  return useQuery({
    queryKey: ['anomaly-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get<AnomalyStats>('/api/v1/anomalies/stats');
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateAnomaly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; status: string; resolution_notes?: string }) => {
      const { id, ...body } = args;
      const { data } = await apiClient.patch(`/api/v1/anomalies/${id}`, body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['anomalies'] });
      qc.invalidateQueries({ queryKey: ['anomaly'] });
      qc.invalidateQueries({ queryKey: ['anomaly-stats'] });
    },
  });
}

export function useTriggerRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/api/v1/anomalies/trigger-rules');
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['anomalies'] });
      qc.invalidateQueries({ queryKey: ['anomaly-stats'] });
    },
  });
}

export function useRuleConfigs() {
  return useQuery({
    queryKey: ['rule-configs'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: RuleConfig[] }>('/api/v1/anomalies/rules');
      return data.items;
    },
    staleTime: 5 * 60_000,
  });
}
